<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>
    <warehouse-write-off-dialog
      v-model="writeOffDialog"
      :edited-item="editedItem"
      @groceriesWrittenOff="addDocuments"
      @success="onSuccess"
    />

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredDocuments.length > 0 || loading">
        <mobile-data-cards
          v-for="(group, warehouseId) in documentsByWarehouse"
          :key="warehouseId"
          :headers="mobileHeaders"
          :items="group.items"
          :loading="loading"
          :title="toLatin(group.warehouseName)"
          item-key="id"
          show-filters
          :fields-per-row="null"
          :show-row-number="false"
          :bold-fields="['number']"
        >
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters justify="end">
              <circle-button
                @click="editItem(item)"
                icon="mdi-eye"
                :tooltip="$t('overview')"
                color="primary"
                class="mr-2"
              />
              <remove-button @click="deleteItem(item)"></remove-button>
            </v-row>
          </template>
        </mobile-data-cards>
      </template>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-delete-sweep-outline"
        :title="$t('no_write_offs')"
        :subtitle="$t('no_write_offs_yet')"
      >
        <warehouse-button-trash @click="openWriteOffDialog"></warehouse-button-trash>
      </empty-state>

      <div class="fixed-bottom-button">
        <v-btn color="error" block @click="openWriteOffDialog">
          <v-icon class="mr-2">mdi-trash-can-outline</v-icon>
          {{ $t("warehouse.write-off-product") }}
        </v-btn>
      </div>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <v-data-table
        v-if="filteredDocuments.length > 0 || loading"
        :headers="headers"
        :items="filteredDocuments"
        class="ordera-box-shadow striped-table"
        :loading="loading"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-spacer></v-spacer>
            <warehouse-button-trash @click="openWriteOffDialog"></warehouse-button-trash>
          </v-toolbar>
        </template>
        <template v-slot:item.warehouse.name="{ item }">
          {{ toLatin(item.warehouse?.name) || "-" }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <circle-button
              @click="editItem(item)"
              icon="mdi-eye"
              :tooltip="$t('overview')"
              color="primary"
              class="mr-2"
            />
            <remove-button @click="deleteItem(item)"></remove-button>
          </v-row>
        </template>
        <template v-slot:item.document_date="{ item }">
          {{ localeDate(item.document_date) }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{
            localeDate(item.created_at, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          }}
        </template>
      </v-data-table>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-delete-sweep-outline"
        :title="$t('no_write_offs')"
        :subtitle="$t('no_write_offs_yet')"
      >
        <warehouse-button-trash @click="openWriteOffDialog"></warehouse-button-trash>
      </empty-state>
    </template>
  </div>
</template>

<script>
import { WarehouseButtonTrash } from "@/components/warehouse/buttons";
import WarehouseWriteOffDialog from "@/components/warehouse/writeOff/dialogs/WarehouseWriteOffDialog.vue";
import { writeOffDocumentGrocery } from "@/models/warehouse";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";

export default {
  components: {
    WarehouseButtonTrash,
    WarehouseWriteOffDialog,
    EmptyState,
    MobileDataCards,
  },
  mixins: [hasPrinting, hasWarehousePrinting, hasExcel, hasSnackbar],
  data: () => ({
    writeOffDialog: false,
    documents: [],
    editedItem: writeOffDocumentGrocery,
    loading: false,
  }),

  created() {
    this.load();
  },

  methods: {
    onSuccess() {
      this.load();
      this.$store.commit("warehouse/TRIGGER_REFRESH");
    },
    addDocuments(documents) {
      this.documents.push(...documents);
      this.showSuccessSnackbar(this.$t("write_off_documents_added"));
    },
    openWriteOffDialog() {
      this.editedItem = Object.assign(
        {},
        { ...writeOffDocumentGrocery, warehouse: this.filters.warehouse[0] }
      );
      this.writeOffDialog = true;
    },

    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.writeOffDialog = true;
    },

    async deleteItem(item) {
      const confirmed = await this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_write_off"),
          this.$t("warehouse.confirm_delete_write_off"),
          {
            color: "warning",
          }
        )
        .catch(() => false);

      if (confirmed) {
        try {
          await warehouseService.removeWarehouse(item.id);
          this.documents = this.documents.filter((i) => i.id !== item.id);
          this.showSuccessSnackbar(this.$t("write_off_document_deleted"));
        } catch (err) {
          this.showErrorSnackbar(this.$t("error_deleting_write_off_document"));
          console.warn(err);
        }
      }
    },

    async load() {
      this.loading = true;
      try {
        const res = await warehouseService.getWarehouseDocuments({
          document_type: "write-off",
        });
        this.documents = res.data;
      } catch (e) {
        this.showErrorSnackbar(this.$t("error_loading_write_off_documents"));
      } finally {
        this.loading = false;
      }
    },
  },

  computed: {
    translatedDocuments() {
      return this.documents.map((document) => {
        return {
          ...document,
          number: this.translate(document.number),
          notes: this.translate(document.notes),
        };
      });
    },
    filteredDocuments() {
      return this.customSearch(
        this.translatedDocuments,
        this.headers,
        this.filters.search
      ).filter((item) => {
        const { from, to } = this.filters.dates;
        if (!from || !to) return true;

        const documentDate = new Date(item.document_date + "T12:00:00");
        const fromDate = new Date(from + "T00:00:00");
        const toDate = new Date(to + "T23:59:59");
        const warehouseFilter = this.filters.warehouse.some(
          (warehouse) => warehouse.id === item.warehouse_id
        );
        return documentDate >= fromDate && documentDate <= toDate && warehouseFilter;
      });
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    headers() {
      return [
        { text: "ID", value: "id", mobile: false },
        { text: this.$t("stock"), value: "warehouse.name", mobile: false },
        { text: this.$t("warehouse.document_number"), value: "number", cols: 6 },
        { text: this.$t("warehouse.write-off_date"), value: "document_date", cols: 6 },
        { text: this.$t("note"), value: "notes", cols: 9 },
        { text: this.$t("status_created"), value: "created_at", mobile: false, cols: 9 },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          print: false,
          cols: 3,
          width: "100px",
          align: "right",
        },
      ];
    },
    mobileHeaders() {
      return this.headers.filter((h) => h.mobile !== false);
    },
    documentsByWarehouse() {
      const groups = {};
      this.filteredDocuments.forEach((item) => {
        const warehouseId = item.warehouse_id;
        if (!groups[warehouseId]) {
          groups[warehouseId] = {
            warehouseName: item.warehouse?.name || this.$t("stock"),
            items: [],
          };
        }
        groups[warehouseId].items.push(item);
      });
      return groups;
    },
    exportTitle() {
      return [
        this.filters.warehouse.map((warehouse) => warehouse.name).join(", ") ||
          this.$t("stock"),
        this.$t("warehouse.write_off").toLowerCase(),
      ].join(" ");
    },
  },
  watch: {
    "$store.state.warehouse.refreshTrigger"() {
      this.load();
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: this.exportTitle + ".xlsx",
          sheets: [
            {
              name: this.exportTitle,
              headers: this.headers,
              items: this.filteredDocuments,
              title: this.exportTitle,
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
    isPrinting(isPrinting) {
      if (isPrinting) {
        this.printDocument({
          title: this.exportTitle,
          subtitle: this.subtitle,
          tables: [
            {
              headers: this.headers,
              items: this.filteredDocuments,
            },
          ],
        });
      }
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  background: white;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 5;
}
</style>
